import { faCheckSquare, faSquare } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import React from "react";
import { Field, FormErrors } from "redux-form";

import { UNCATEGORIZED_TOPIC_ID } from "@/mk2/apps/forum/constants";
import { TopicEntity } from "@/mk2/apps/forum/schemas";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { Form } from "@/mk2/components/Form";
import { Interpolate } from "@/mk2/components/Interpolate";
import { ClickableField, HideArrow } from "@/mk2/components/forms/ClickableField";
import { TextareaField } from "@/mk2/components/forms/TextareaField";
import { FormPageType } from "@/mk2/constants/enums";
import { ComposeToolbar, FIXED } from "@/mk2/containers/Toolbar/Toolbar";
import { MKReduxFormInjectedProps, mkReduxForm } from "@/mk2/decorators/mkReduxForm";
import { Photo } from "@/mk2/decorators/withUploader";
import {
    ANONYMOUS_DISCUSSION_EXPLANATION3,
    ANONYMOUS_DISCUSSION_EXPLANATION4,
    ANONYMOUS_QUESTION_TO_counselling,
    COUNSELLING_QUESTION_PEREX,
    COUNSELLING_QUESTION_TITLE,
    CREATE_POST_TO_topic,
    DEPICTION_SETTINGS,
    FORMS_DEFAULT_REQUIRED_FIELD_ERROR,
    FORUM_POST_AUTHOR_DISPLAY,
    FORUM_POST_WANTS_MK_TO_FOUND_DISCUSSION,
    FORUM_POST_YOU_DID_NOT_ENTER_TITLE,
    KEEP_SHOWING_MY_USERNAME,
    POST_HEADING,
    POST_PEREX,
    TESTING_ID,
    YOUR_QUESTION_WILL_BE_SEND_FOR_APPROVING_AFTERWARDS_WILL_BE_PUBLISHED_ANONYMOUSLY,
} from "@/mk/autogenerated/translations/PostCreateEditForm.e8a3d2c63967bf13023ce80c84456ed1";

import styles from "./PostCreateEditForm.mscss";

export const POST_CREATE_EDIT_FORM_NAME = "POST_CREATE_EDIT_FORM";

export interface PostFormData {
    topic?: TopicEntity;
    title: string;
    perex: string;
    photos: Photo[];
    sticker: number;
    testingId?: number;
    anonymousMode?: string; // 'enabled' or 'disabled'
}

interface OwnProps {
    currentFormPage?: FormPageType;
    isMobile: boolean;
    initialValues: PostFormData;
    showTopic: boolean;
    isCounselling?: boolean;
    isTitleEditableOnceAgain?: boolean;
    counsellingName?: string;
    isTesting?: boolean;
    isUnauthenticatedUser?: boolean;
    anonymousMode?: boolean;
    wantsMkToFoundDiscussion?: boolean;
    requestUserPermissions: string[];

    onDisableAnonymousMode?();
    onEnableAnonymousMode?();
    onSetWantsMkToFoundDiscussion?(enabled: boolean): void;
    onSave(values: PostFormData);
}

type Props = OwnProps;

const emojisConfig = {
    fields: ["title", "perex"],
    defaultField: "perex",
};

const photosConfig = {
    photoType: "forum",
    multiple: true,
    showPhotoCode: false,
};

class PostCreateEditFormComponent extends React.Component<
    Props & MKReduxFormInjectedProps<PostFormData, Props>
> {
    public static validate(values: PostFormData, props: Props): FormErrors<PostFormData> {
        const errors: FormErrors<PostFormData> = {};

        if (!values.title) {
            errors.title = FORUM_POST_YOU_DID_NOT_ENTER_TITLE;
        }
        if (props.isTesting && (!values.testingId || isNaN(values.testingId))) {
            errors.testingId = FORMS_DEFAULT_REQUIRED_FIELD_ERROR;
        }

        return errors;
    }

    public render() {
        const { currentFormPage } = this.props;

        return currentFormPage === FormPageType.AnonymousModeExplanation
            ? this.renderAnonymousModeExplanation()
            : this.renderForm();
    }

    public renderForm() {
        const {
            isMobile,
            showTopic,
            handleSubmit,
            initialValues,
            isCounselling,
            isTesting,
            isUnauthenticatedUser,
            counsellingName,
            requestUserPermissions,
            isTitleEditableOnceAgain: enableOnlyTitle,
        } = this.props;
        const topic = initialValues.topic;
        const isUncategorized = topic && topic.id === UNCATEGORIZED_TOPIC_ID;
        const isAdmin = requestUserPermissions.indexOf("forum.can_manage_forum") >= 0;

        return (
            <Form className={styles.PostCreateEditForm__form} onSubmit={handleSubmit}>
                {showTopic && !isCounselling && !isUncategorized && (
                    <ClickableField
                        name="topic"
                        ValueComponent={this.renderTopic}
                        hideArrow={HideArrow.IfValue}
                        disabled={enableOnlyTitle}
                    />
                )}
                {isCounselling && (
                    <ClickableField
                        name="dummy-counselling-field"
                        onClick={null}
                        ValueComponent={this.renderCounselling(counsellingName)}
                        hideArrow={HideArrow.Always}
                        disabled={enableOnlyTitle}
                    />
                )}
                <TextareaField
                    name="title"
                    label={isCounselling ? COUNSELLING_QUESTION_TITLE : POST_HEADING}
                    maxLength={100}
                    rows={1}
                    hasLargeFont
                    autoFocus
                />
                {isTesting && (
                    <TextareaField
                        name="testingId"
                        label={TESTING_ID}
                        maxLength={4}
                        rows={1}
                        bottomBorder
                        disabled={enableOnlyTitle}
                    />
                )}
                <TextareaField
                    name="perex"
                    label={isCounselling ? COUNSELLING_QUESTION_PEREX : POST_PEREX}
                    rows={isMobile ? 10 : 25}
                    bottomBorder={isAdmin}
                    fillRemainingSpace
                    disabled={enableOnlyTitle}
                />
                {isCounselling && (
                    <div className={styles.PostCreateEditForm__counsellingInfo}>
                        {
                            YOUR_QUESTION_WILL_BE_SEND_FOR_APPROVING_AFTERWARDS_WILL_BE_PUBLISHED_ANONYMOUSLY
                        }
                    </div>
                )}

                <Field component="input" name="anonymousMode" type="hidden" />

                {!enableOnlyTitle && (
                    <ComposeToolbar
                        formName={POST_CREATE_EDIT_FORM_NAME}
                        isFixed={FIXED.IF_MOBILE_BUT_IOS}
                        emojis={emojisConfig}
                        photos={isUnauthenticatedUser ? undefined : photosConfig}
                    />
                )}
            </Form>
        );
    }

    private renderTopic = (props: { value: TopicEntity }) => (
        <Interpolate
            className={styles.PostCreateEditForm__topic}
            i18nKey={CREATE_POST_TO_topic}
            topic={<strong>{props.value.name}</strong>}
        />
    );

    private renderCounselling = (counsellingName) => {
        return () => (
            <Interpolate
                i18nKey={ANONYMOUS_QUESTION_TO_counselling}
                counselling={<strong>{counsellingName}</strong>}
            />
        );
    };

    private renderAnonymousModeExplanation = () => {
        const { anonymousMode, wantsMkToFoundDiscussion } = this.props;
        return (
            <div className={styles.PostCreateEditForm__anonymous}>
                <h2 className={styles.PostCreateEditForm__anonymous__heading}>
                    {DEPICTION_SETTINGS}
                </h2>
                <p>{ANONYMOUS_DISCUSSION_EXPLANATION3}</p>
                <p dangerouslySetInnerHTML={{ __html: ANONYMOUS_DISCUSSION_EXPLANATION4 }} />
                <p>
                    <label
                        className={styles.PostCreateEditForm__anonymous__label}
                        htmlFor="showUsername"
                    >
                        <div className={styles.PostCreateEditForm__anonymous__label__icon}>
                            <FontAwesomeIcon icon={!anonymousMode ? faCheckSquare : faSquare} />
                        </div>
                        <input
                            checked={!anonymousMode}
                            className={styles.PostCreateEditForm__anonymous__label__input}
                            id="showUsername"
                            type="checkbox"
                            onChange={this.handleShowUsernameChange}
                        />
                        {KEEP_SHOWING_MY_USERNAME}
                    </label>
                </p>
                <h2
                    className={cx(
                        styles.PostCreateEditForm__anonymous__heading,
                        styles.PostCreateEditForm__marginTop,
                    )}
                >
                    {FORUM_POST_AUTHOR_DISPLAY}
                </h2>
                <Btn
                    type={BtnType.BlueOutline}
                    onClick={this.handleWantsMkToFoundDiscussionChange}
                    label={FORUM_POST_WANTS_MK_TO_FOUND_DISCUSSION}
                />
            </div>
        );
    };

    private handleShowUsernameChange = (event: React.FormEvent<HTMLInputElement>) => {
        const { onDisableAnonymousMode, onEnableAnonymousMode } = this.props;

        if (event.currentTarget.checked) {
            onDisableAnonymousMode();
        } else {
            onEnableAnonymousMode();
        }
    };

    private handleWantsMkToFoundDiscussionChange = () => {
        this.props.onSetWantsMkToFoundDiscussion?.(true);
    };
}

export const PostCreateEditForm = mkReduxForm<PostFormData, Props>({
    formName: POST_CREATE_EDIT_FORM_NAME,
    photosConfig,
    onValidate: (values, props) => PostCreateEditFormComponent.validate(values, props),
    onSave: (values, props) => props.onSave(values),
})(PostCreateEditFormComponent);
